import { DirectionProvider as RadixDirectionProvider } from '@radix-ui/react-direction';
import * as React from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export const fonts = ['geist', 'inter', 'manrope', 'system'] as const;

export type Font = (typeof fonts)[number];
export type Theme = 'dark' | 'light' | 'system';
export type ResolvedTheme = Exclude<Theme, 'system'>;
export type Direction = 'ltr' | 'rtl';
export type LayoutCollapsible = 'offcanvas' | 'icon' | 'none';
export type LayoutVariant = 'inset' | 'sidebar' | 'floating';

export const DEFAULT_THEME: Theme = 'system';
export const DEFAULT_FONT: Font = 'inter';
export const DEFAULT_DIRECTION: Direction = 'ltr';
export const DEFAULT_LAYOUT_COLLAPSIBLE: LayoutCollapsible = 'offcanvas';
export const DEFAULT_LAYOUT_VARIANT: LayoutVariant = 'sidebar';

const COLOR_SCHEME_QUERY = '(prefers-color-scheme: dark)';

type UiStoreState = {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  font: Font;
  dir: Direction;
  collapsible: LayoutCollapsible;
  variant: LayoutVariant;
  searchOpen: boolean;
  /** Last visited pathname outside any sidebar view; target of the view back link. */
  lastRootPath: string | null;
  setTheme: (theme: Theme) => void;
  resetTheme: () => void;
  setResolvedTheme: (theme: ResolvedTheme) => void;
  setFont: (font: Font) => void;
  resetFont: () => void;
  setDir: (dir: Direction) => void;
  resetDir: () => void;
  setCollapsible: (collapsible: LayoutCollapsible) => void;
  setVariant: (variant: LayoutVariant) => void;
  resetLayout: () => void;
  setSearchOpen: (open: boolean) => void;
  toggleSearchOpen: () => void;
  setLastRootPath: (path: string) => void;
};

function getSystemTheme(): ResolvedTheme {
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia(COLOR_SCHEME_QUERY).matches ? 'dark' : 'light';
  }

  return 'light';
}

function disableTransitionsTemporarily() {
  const style = document.createElement('style');
  style.appendChild(
    document.createTextNode(
      '*,*::before,*::after{-webkit-transition:none!important;transition:none!important}',
    ),
  );
  document.head.appendChild(style);

  return () => {
    window.getComputedStyle(document.body);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        style.remove();
      });
    });
  };
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.isContentEditable) {
    return true;
  }

  return Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
}

export const useUiStore = create<UiStoreState>()(
  persist(
    (set) => ({
      theme: DEFAULT_THEME,
      resolvedTheme: getSystemTheme(),
      font: DEFAULT_FONT,
      dir: DEFAULT_DIRECTION,
      collapsible: DEFAULT_LAYOUT_COLLAPSIBLE,
      variant: DEFAULT_LAYOUT_VARIANT,
      searchOpen: false,
      lastRootPath: null,
      setTheme: (theme) =>
        set({
          theme,
          resolvedTheme: theme === 'system' ? getSystemTheme() : theme,
        }),
      resetTheme: () =>
        set({
          theme: DEFAULT_THEME,
          resolvedTheme: getSystemTheme(),
        }),
      setResolvedTheme: (resolvedTheme) => set({ resolvedTheme }),
      setFont: (font) => set({ font }),
      resetFont: () => set({ font: DEFAULT_FONT }),
      setDir: (dir) => set({ dir }),
      resetDir: () => set({ dir: DEFAULT_DIRECTION }),
      setCollapsible: (collapsible) => set({ collapsible }),
      setVariant: (variant) => set({ variant }),
      resetLayout: () =>
        set({
          collapsible: DEFAULT_LAYOUT_COLLAPSIBLE,
          variant: DEFAULT_LAYOUT_VARIANT,
        }),
      setSearchOpen: (searchOpen) => set({ searchOpen }),
      toggleSearchOpen: () => set((state) => ({ searchOpen: !state.searchOpen })),
      setLastRootPath: (lastRootPath) => set({ lastRootPath }),
    }),
    {
      name: 'ui-kit-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        theme: state.theme,
        font: state.font,
        dir: state.dir,
        collapsible: state.collapsible,
        variant: state.variant,
      }),
    },
  ),
);

export function useThemeState() {
  const theme = useUiStore((state) => state.theme);
  const resolvedTheme = useUiStore((state) => state.resolvedTheme);
  const setTheme = useUiStore((state) => state.setTheme);
  const resetTheme = useUiStore((state) => state.resetTheme);

  return {
    defaultTheme: DEFAULT_THEME,
    theme,
    resolvedTheme,
    setTheme,
    resetTheme,
  };
}

export function useFontState() {
  const font = useUiStore((state) => state.font);
  const setFont = useUiStore((state) => state.setFont);
  const resetFont = useUiStore((state) => state.resetFont);

  return { font, setFont, resetFont };
}

export function useDirectionState() {
  const dir = useUiStore((state) => state.dir);
  const setDir = useUiStore((state) => state.setDir);
  const resetDir = useUiStore((state) => state.resetDir);

  return {
    defaultDir: DEFAULT_DIRECTION,
    dir,
    setDir,
    resetDir,
  };
}

export function useLayoutState() {
  const collapsible = useUiStore((state) => state.collapsible);
  const setCollapsible = useUiStore((state) => state.setCollapsible);
  const variant = useUiStore((state) => state.variant);
  const setVariant = useUiStore((state) => state.setVariant);
  const resetLayout = useUiStore((state) => state.resetLayout);

  return {
    defaultCollapsible: DEFAULT_LAYOUT_COLLAPSIBLE,
    collapsible,
    setCollapsible,
    defaultVariant: DEFAULT_LAYOUT_VARIANT,
    variant,
    setVariant,
    resetLayout,
  };
}

export function useSearchState() {
  const open = useUiStore((state) => state.searchOpen);
  const setOpen = useUiStore((state) => state.setSearchOpen);
  const toggleOpen = useUiStore((state) => state.toggleSearchOpen);

  return { open, setOpen, toggleOpen };
}

export function UiStoreRoot({ children }: { children: React.ReactNode }) {
  const dir = useUiStore((state) => state.dir);
  const font = useUiStore((state) => state.font);
  const theme = useUiStore((state) => state.theme);
  const setResolvedTheme = useUiStore((state) => state.setResolvedTheme);
  const toggleSearchOpen = useUiStore((state) => state.toggleSearchOpen);

  React.useEffect(() => {
    const root = document.documentElement;
    root.classList.forEach((className) => {
      if (className.startsWith('font-')) {
        root.classList.remove(className);
      }
    });
    root.classList.add(`font-${font}`);
  }, [font]);

  React.useEffect(() => {
    document.documentElement.setAttribute('dir', dir);
  }, [dir]);

  React.useEffect(() => {
    const root = document.documentElement;
    const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;
    const restoreTransitions = disableTransitionsTemporarily();

    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
    setResolvedTheme(resolvedTheme);
    restoreTransitions();

    if (theme !== 'system') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(COLOR_SCHEME_QUERY);
    const handleChange = () => {
      setResolvedTheme(getSystemTheme());
      root.classList.remove('light', 'dark');
      root.classList.add(getSystemTheme());
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [setResolvedTheme, theme]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) {
        return;
      }

      if (isEditableTarget(event.target)) {
        return;
      }

      // ⚠️ Aici stătea şi „`d` comută tema", din shadcn-admin. **Scoasă** (fondatorul,
      // 2026-08-19): o tastă simplă, fără modificator, care schimbă tot ecranul e prea
      // uşor de apăsat din greşeală — şi intră direct peste secvenţele de navigare
      // („G apoi S apoi D" duce la tipurile de document). Tema se schimbă din paletă şi
      // din meniul de utilizator, unde e vizibilă ca alegere.
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        toggleSearchOpen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleSearchOpen]);

  return <RadixDirectionProvider dir={dir}>{children}</RadixDirectionProvider>;
}
